import { mediaQueryOnly } from 'components/utils/breakpoint'
import { useAppSelector } from 'hooks/reduxHooks'
import { memo, useCallback, useContext, useMemo, useRef } from 'react'
import { getFlatNavItems } from 'selectors/navigationSelectors'
import useLocalizedNavItems from 'hooks/useLocalizedNavItems'
import styled from 'styled-components'
import { rem } from 'polished'
import NavBarTab from '../NavBarTab/NavBarTab'
import Group from 'components/utils/Group'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import useToggle from 'hooks/useToggle'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import ModalContext from 'contexts/ModalContext'
import Modal from 'LuxKit/Modal/Modal'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import { useIntl } from 'react-intl'

const MoreNavTab = styled(NavBarTab)`
  display: none;
`

/**
 * We hide products via pure CSS. This is done to ensure 100% SSR matching behaviour
 * in both the tabs and the dropdown.
 *
 * The following breakpoints allow these number of items:
 * Mobile -> 4
 * Tablet -> 4
 * Desktop -> 5
 * Large desktop -> 8
 * Extra lareg desktop -> 10
 *
 * We then show the 'more' button if there would be items after this
 */
const ProductTabs = styled(Group)`
  ${mediaQueryOnly.mobile} {
    & >:nth-child(n + 5) {
      display: none;

      &${MoreNavTab}:nth-child(n + 6) {
        display: flex;
      }
    }
  }

  ${mediaQueryOnly.tablet} {
    & >:nth-child(n + 5) {
      display: none;

      &${MoreNavTab}:nth-child(n + 6) {
        display: flex;
      }
    }
  }

  ${mediaQueryOnly.desktop} {
    & >:nth-child(n + 6) {
      display: none;

      &${MoreNavTab}:nth-child(n + 7) {
        display: flex;
      }
    }
  }

  ${mediaQueryOnly.largeDesktop} {
    & >:nth-child(n + 9) {
      display: none;

      &${MoreNavTab}:nth-child(n + 10) {
        display: flex;
      }
    }
  }

  ${mediaQueryOnly.extraLargeDesktop} {
    & >:nth-child(n + 11) {
      display: none;

      &${MoreNavTab}:nth-child(n + 12) {
        display: flex;
      }
    }
  }
`

const NavListItem = styled(ClickableListItem)`
  ${mediaQueryOnly.mobile} {
    &:not(:nth-child(n + 5)) {
      display: none;
    }
  }

  ${mediaQueryOnly.tablet} {
    &:not(:nth-child(n + 5)) {
      display: none;
    }
  }

  ${mediaQueryOnly.desktop} {
    &:not(:nth-child(n + 6)) {
      display: none;
    }
  }

  ${mediaQueryOnly.largeDesktop} {
    &:not(:nth-child(n + 9)) {
      display: none;
    }
  }

  ${mediaQueryOnly.extraLargeDesktop} {
    &:not(:nth-child(n + 11)) {
      display: none;
    }
  }
`

const NewLabelNavListItem = styled(NavListItem)`
  &::after {
    content: attr(data-new-label);
    flex-shrink: 0;
    background-color: ${props => props.theme.palette.messaging.critical.normalBackground};
    color: ${props => props.theme.palette.messaging.critical.normalContrast};
    padding: ${rem(2)} ${rem(8)};
    border-radius: ${props => props.theme.borderRadius.round};
    font-weight: ${props => props.theme.font.primary.weight.bold};
    font-size: ${rem(12)};
    line-height: ${rem(16)};
  }
`

function NavBarProducts() {
  const intl = useIntl()
  const [open, toggleOpen, _, closeDropdown] = useToggle()
  const isMobile = useScreenSizeOnly('mobile')
  const moreTabRef = useRef<HTMLButtonElement>(null)
  const rawNavs = useAppSelector(getFlatNavItems)
  const navs = useLocalizedNavItems(rawNavs)
  const showModal = useContext(ModalContext)

  const newLabelText = useMemo(() => intl.formatMessage({
    id: 'ULXt7xsBZxh6l90xv4YH',
    defaultMessage: 'NEW',
    description: 'New badge label for navigation items in the site header',
  }), [intl])

  const moreTab: App.NavItem = useMemo(
    () => ({
      text: intl.formatMessage({
        id: 'tu0LauZOJipCPjr6G-Vw',
        defaultMessage: 'More',
        description: 'Navigation link label for the more dropdown in the navigation bar',
      }),
      category: 'tertiary',
    }),
    [intl],
  )

  const moreModalTitle = useMemo(
    () =>
      intl.formatMessage({
        id: 'tu0LauZOJipCPjr6G-Vw',
        defaultMessage: 'More',
        description: 'Navigation link label for the more dropdown in the navigation bar',
      }),
    [intl],
  )
  useOptimizelyExperiment(OptimizelyExperiments.navHomesVillasBeforeMaldivesBundles)

  const onNavClick = useCallback((_nav: App.NavItem, onClose?: () => void) => {
    if (onClose) {
      onClose()
    }
  }, [])

  const onMoreClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>(() => {
    if (isMobile) {
      showModal(resolve => <Modal title={moreModalTitle}>
        {navs.map((item, index) => {
          const ListItem = item.newLabel ? NewLabelNavListItem : NavListItem
          return <ListItem
            size="large"
            key={index}
            title={item.text}
            to={item.to}
            href={item.href}
            data-new-label={item.newLabel ? newLabelText : undefined}
            onClick={() => onNavClick(item, resolve)}
          />
        })}
      </Modal>)
    } else {
      toggleOpen()
    }
  }, [isMobile, navs, onNavClick, showModal, toggleOpen, moreModalTitle, newLabelText])

  return <Group direction="horizontal">
    <ProductTabs direction="horizontal">
      {navs.map(nav => <NavBarTab
        key={nav.id}
        nav={nav}
        onClick={() => onNavClick(nav)}
      />)}
      <MoreNavTab
        onClick={onMoreClick}
        nav={moreTab}
        ref={moreTabRef}
        isDropdown
      />
    </ProductTabs>
    <DropdownList
      size="S"
      triggerRef={moreTabRef}
      anchorRef={moreTabRef}
      open={!isMobile && open}
      onClose={closeDropdown}
      portaled
    >
      {navs.map((item, index) => {
        const ListItem = item.newLabel ? NewLabelNavListItem : NavListItem
        return <ListItem
          size="medium"
          key={index}
          title={item.text}
          to={item.to}
          href={item.href}
          data-new-label={item.newLabel ? newLabelText : undefined}
          onClick={() => onNavClick(item, closeDropdown)}
        />
      })}
    </DropdownList>
  </Group>
}

export default memo(NavBarProducts)
